import {Link, useParams} from 'react-router-dom';
import {Editor} from '@craftjs/core';
import {Cog6ToothIcon, CubeIcon, RectangleStackIcon, UsersIcon} from '@heroicons/react/24/outline';
import {useGetProject, type ProjectType} from 'api/generated';
import {useChangeParams, useEditorEnabled} from 'hooks';
import {Button, Spinner, Transitioner, editorResolvers, EditorContentParent} from 'components';
import {ProjectStoreProvider} from './stores/useProjectStore';

import Topbar from './components/Topbar';
import Content from './components/Content';
import SidebarRight from './components/Sidebar.Right';
import SidebarEdge from './components/Sidebar.Edge';
import SidebarLeft from './components/Sidebar.Left';

import SidebarLeftAssets from './components/Sidebar.Left/Sidebar.Left.Assets';
import SidebarRightAssets from './components/Sidebar.Right/Sidebar.Right.Assets';

import SidebarLeftEditor from './components/Sidebar.Left/Sidebar.Left.Editor';
import SidebarRightEditor from './components/Sidebar.Right/Sidebar.Right.Editor';

import SidebarLeftTeam from './components/Sidebar.Left/Sidebar.Left.Team';

import SidebarLeftSettings from './components/Sidebar.Left/Sidebar.Left.Settings';

export const NAVIGATIONS = {
  editor: {name: 'Editor', icon: RectangleStackIcon, compLeft: SidebarLeftEditor, compRight: SidebarRightEditor},
  assets: {name: 'Assets', icon: CubeIcon, compLeft: SidebarLeftAssets, compRight: SidebarRightAssets},
  team: {name: 'Team', icon: UsersIcon, compLeft: SidebarLeftTeam, compRight: null},
  settings: {name: 'Settings', icon: Cog6ToothIcon, compLeft: SidebarLeftSettings, compRight: null},
};

interface IPageProjectsUpateProps {}

const PageProjectsUpdate = ({}: IPageProjectsUpateProps) => {
  const params = useParams();
  const workspace = params.workspace;
  const resource = params.resource;
  const uid = params.uid;

  const {data: projectData, isPending} = useGetProject(uid as string, {query: {gcTime: 0}});
  const project = projectData?.data?.data;

  if (isPending) {
    return (
      <div className="min-h-screen flex justify-center py-40">
        <Transitioner delay={0.1}>
          <Spinner />
        </Transitioner>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex justify-center py-40">
        <Transitioner delay={0.1} classNames="flex flex-col items-center">
          <p className="text-center py-5">Project not found, may have been deleted.</p>
          <Link to={`/${workspace}/projects`}>
            <Button text="Go back" rounded />
          </Link>
        </Transitioner>
      </div>
    );
  }

  return <Layout workspace={workspace} resource={resource} project={project} />;
};

export default PageProjectsUpdate;

interface ILayoutProps {
  workspace: string | undefined;
  resource: string | undefined;
  project: ProjectType;
}

const Layout = ({project, workspace, resource}: ILayoutProps) => {
  const {handleChangeParams} = useChangeParams();
  const enabled = useEditorEnabled();
  return (
    <ProjectStoreProvider defaultValue={project}>
      <Editor resolver={editorResolvers} onRender={EditorContentParent} enabled={enabled}>
        <div className="min-h-screen flex flex-col">
          <SidebarEdge workspace={workspace} resource={resource} onChangeParams={handleChangeParams} />
          <Topbar />
          <div className="pl-20 pt-15.5 absolute inset-0 flex flex-1">
            <SidebarLeft />
            <Content />
            <SidebarRight />
          </div>
        </div>
      </Editor>
    </ProjectStoreProvider>
  );
};
